import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Formik } from 'formik'
import * as yup from 'yup'

import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import TextField from '@/components/formik/fields/TextField'
import SwitchField from '@/components/formik/fields/SwitchField'

const CustomizedListItem = styled(ListItem)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    alignItems: 'start',
    flexDirection: 'column',
  },
  paddingLeft: 0,
  paddingRight: 0,
}))

const CustomizedListItemText = styled(ListItemText)({
  flex: 'none',
  width: '40%',
  '& .MuiListItemText-primary': {
    fontSize: '14px',
  },
})

const ListItemContent = styled('div')({
  flex: 1,
})

const schema = yup.object({
  description: yup.string(),
  isPrioritized: yup.boolean(),
})

export type MeetingLinkFormValues = yup.InferType<typeof schema>

type MeetingLinkDrawerProps = {
  isUpdating?: boolean
  initialValues: MeetingLinkFormValues
  onClose?: () => void
  onSubmit: (values: MeetingLinkFormValues) => void
  open: boolean
  title: string
}

export function MeetingLinkDrawer(props: MeetingLinkDrawerProps) {
  const { isUpdating, initialValues, onClose, onSubmit, open, title } = props
  const { t } = useTranslation()

  const [editable, setEditable] = useState(false)

  useEffect(() => {
    if (open) setEditable(false)
  }, [initialValues, open])

  useEffect(() => {
    if (!open) setEditable(false)
  }, [open])

  const toggleEditable = () => {
    setEditable((prevEditable) => !prevEditable)
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose} sx={{ zIndex: (theme) => theme.zIndex.drawer + 10 }}>
      <Stack direction="column" sx={{ height: '100%', minWidth: { xs: '360px', sm: '500px', md: '600px' }, p: '16px 32px' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography component="h2" variant="h2">
            {title}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
        <Formik enableReinitialize initialValues={initialValues} onSubmit={onSubmit}>
          {({ dirty, isValid, handleReset }) => (
            <Form style={{ flex: 1 }}>
              <List sx={{ mt: 2, mb: 2, width: '100%', bgcolor: 'background.paper' }}>
                <CustomizedListItem>
                  <CustomizedListItemText primary="Description" />
                  <ListItemContent>
                    <TextField readOnly={!editable} fullWidth multiline name="description" />
                  </ListItemContent>
                </CustomizedListItem>
                <Divider component="li" />
                <CustomizedListItem>
                  <CustomizedListItemText primary="Display on home page" />
                  <ListItemContent>
                    <SwitchField readOnly={!editable} name="isPrioritized" />
                  </ListItemContent>
                </CustomizedListItem>
              </List>
              <Box>
                {editable ? (
                  <Stack direction="row" spacing={1}>
                    <LoadingButton disabled={!dirty || !isValid} loading={isUpdating} type="submit" variant="contained">
                      {t('common:button.submit.label')}
                    </LoadingButton>
                    <Button
                      onClick={() => {
                        handleReset()
                        setEditable(false)
                      }}
                      variant="neutral"
                    >
                      {t('common:button.cancel.label')}
                    </Button>
                  </Stack>
                ) : (
                  <Button onClick={toggleEditable} variant="contained">
                    {t('common:button.modify.label')}
                  </Button>
                )}
              </Box>
            </Form>
          )}
        </Formik>
      </Stack>
    </Drawer>
  )
}

export default MeetingLinkDrawer
